import { createSlice } from '@reduxjs/toolkit'
import { getMyBetsPlinko, placedBetPlinkoGame, postLightningBoardDetails } from '../slices-thunks/pyramidPlinkoGame.thunk'
import { DEFAULT_PLINKO_LIGHTNING_MODE_BOARD } from '../GameTheme/PyramidPlinkoGame/pyramidPlinkogame.constants'
import { BET_RESULT, CURRENCIES, CURRENCY_SYMBOL, DEFAULT_PAGE_CALLS } from 'constants/index'
import { plus, minus, divide } from 'number-precision'
import { moment } from 'config/moment.config'
import { setPayoutValue } from '../GameTheme/PyramidPlinkoGame/pixi-js-scripts/bridge'

const initialState = {
  placedBetData: null,
  betLock: false,
  placedBets: [],
  betLoading: false,
  myBetsData: {
    count: 0,
    rows: []
  },
  liveBets: {
    count: 0,
    rows: []
  },
  myBetsLoading: false,
  lightningBoardLoading: false,
  lightningBoardBallMultipliers: DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.betMultipliers,
  lightningBoardPayouts: DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.payouts,
  wins: 0,
  losses: 0,
  profit: 0,
  wagered: 0,
  showWinAmt: false,
  demoMyBets: []
}

const {
  actions: {
    setBetLock,
    setLightningBoardDetails,
    appendMyBetsPlinko,
    setLiveBets,
    setPyramidPlinkoLossCount,
    setPyramidPlinkoWinsCount,
    setPyramidPlinkoProfit,
    setPyramidPlinkoWagered,
    demoPlacedBetPyramidPlinkoGame,
    setDemoMyBets,
    setShowWinAmt
  },
  reducer
} = createSlice({
  name: 'pyramidPlinkoGame',
  initialState,
  reducers: {
    setLiveBets: (state, action) => {
      return {
        ...state,
        liveBets: {
          count: action.payload?.count ?? 0,
          rows: action.payload?.rows ?? []
        }
      }
    },
    setPyramidPlinkoWinsCount: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          wins: action.payload.reset
        }
      }
      return {
        ...state,
        wins: state.wins + 1
      }
    },
    setPyramidPlinkoLossCount: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          losses: action.payload.reset
        }
      }
      return {
        ...state,
        losses: state.losses + 1
      }
    },
    setPyramidPlinkoProfit: (state, action) => {
      return {
        ...state,
        profit: action.payload
      }
    },
    setPyramidPlinkoWagered: (state, action) => {
      return {
        ...state,
        wagered: action.payload
      }
    },
    setBetLock: (state, action) => {
      return {
        ...state,
        betLock: false
      }
    },
    setLightningBoardDetails: (state, action) => {
      return {
        ...state,
        lightningBoardBallMultipliers: action.payload?.lightningBoardBallMultipliers ?? DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.betMultipliers,
        lightningBoardPayouts: action.payload?.lightningBoardPayouts ?? DEFAULT_PLINKO_LIGHTNING_MODE_BOARD.payouts
      }
    },
    appendMyBetsPlinko: (state, action) => {
      let _placedBets = [...(state?.placedBets ?? [])]
      const _betToAppend = _placedBets?.[_placedBets.length - 1]
      _placedBets = _placedBets?.slice(0, _placedBets.length - 1)
      const { nextServerSeedHash, ...data } = _betToAppend || { nextServerSeedHash: '', data: {} }
      if (nextServerSeedHash) {
        const myBetsDataRows = [data, ...state.myBetsData?.rows]
        if (myBetsDataRows.length > DEFAULT_PAGE_CALLS) {
          myBetsDataRows.pop()
        }
        const profit = plus(state.profit, minus(_betToAppend?.winningAmount ?? 0, _betToAppend?.betAmount ?? 0))
        const wagered = plus(state.wagered, _betToAppend?.betAmount)
        const wins = plus(state.wins, _betToAppend?.result === BET_RESULT.WON ? 1 : 0)
        const losses = plus(state.losses, _betToAppend?.result === BET_RESULT.LOST ? 1 : 0)

        return {
          ...state,
          placedBets: _placedBets,
          myBetsData: {
            count: state.myBetsData?.count + 1,
            rows: myBetsDataRows
          },
          profit,
          wagered,
          wins,
          losses
        }
      }
      return state
    },
    demoPlacedBetPyramidPlinkoGame: (state, action) => {
      const winningAmount = `${CURRENCY_SYMBOL[CURRENCIES[action?.payload?.currencyId]]} ${+(action?.payload?.winningAmount)}`
      const multiplier = `${(+(divide(+action?.payload?.winningAmount, action?.payload?.betAmount)))?.toFixed(2)}x`
      setPayoutValue(winningAmount, multiplier)
      return ({
        ...state,
        placedBets: [action.payload, ...(state?.placedBets ?? [])],
        placedBetData: action.payload,
        betLoading: false,
        betLock: true
      })
    },
    setShowWinAmt: (state, action) => {
      return {
        ...state,
        showWinAmt: action.payload
      }
    },
    setDemoMyBets: (state, action) => {
      let _placedBets = [...(state?.placedBets ?? [])]
      const _betToAppend = _placedBets?.[_placedBets.length - 1]
      _placedBets = _placedBets?.slice(0, _placedBets.length - 1)
      const { nextServerSeedHash, ...data } = _betToAppend || { nextServerSeedHash: '', data: {} }
      if (_betToAppend) {
        const myBetsDataRows = [{ ...data, updatedAt: moment().format() }, ...(state?.demoMyBets ?? [])]
        return {
          ...state,
          placedBets: _placedBets,
          ...(_placedBets?.length === 0
            ? {
                betLock: false
              }
            : {}),
          demoMyBets: myBetsDataRows
        }
      }
      return state
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(placedBetPlinkoGame.pending, (state, action) => {
        return {
          ...state,
          betLock: true,
          betLoading: true
        }
      })
      .addCase(placedBetPlinkoGame.fulfilled, (state, action) => {
        return {
          ...state,
          placedBets: [action.payload, ...(state?.placedBets ?? [])],
          placedBetData: action.payload,
          betLoading: false
        }
      })
      .addCase(placedBetPlinkoGame.rejected, (state, action) => {
        return {
          ...state,
          betLock: false,
          betLoading: false
        }
      })
      .addCase(getMyBetsPlinko.pending, (state, action) => {
        return {
          ...state,
          myBetsData: action.payload,
          myBetsLoading: true

        }
      })
      .addCase(getMyBetsPlinko.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsData: action.payload,
          myBetsLoading: false

        }
      })
      .addCase(postLightningBoardDetails.pending, (state, action) => {
        return {
          ...state,
          lightningBoardLoading: true

        }
      })
      .addCase(postLightningBoardDetails.fulfilled, (state, action) => {
        const lightningBoardDetails = action.payload?.lightningBoardDetails
        return {
          ...state,
          lightningBoardBallMultipliers: lightningBoardDetails?.betMultipliers,
          lightningBoardPayouts: lightningBoardDetails?.payouts,
          lightningBoardLoading: false
        }
      })
      .addCase(postLightningBoardDetails.rejected, (state, action) => {
        return {
          ...state,
          lightningBoardLoading: false
        }
      })
  }
})

export {
  setBetLock,
  setLightningBoardDetails,
  appendMyBetsPlinko,
  setLiveBets,
  setPyramidPlinkoLossCount,
  setPyramidPlinkoWinsCount,
  setPyramidPlinkoProfit,
  setPyramidPlinkoWagered,
  demoPlacedBetPyramidPlinkoGame,
  setDemoMyBets,
  setShowWinAmt
}

export default reducer
